import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Search, FileText, Plus } from "lucide-react";
import QuoteRowActions from "@/components/quote/QuoteRowActions";
import QuoteAssignedBadge from "@/components/quote/QuoteAssignedBadge";
import NewQuoteProjectModal from "@/components/quote/NewQuoteProjectModal";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { hasRole } from "@/lib/roles";

const STATUS_LABELS = {
  brouillon: { label: "Brouillon", cls: "badge-neutral" },
  envoye: { label: "Envoyé", cls: "badge-warning" },
  signe: { label: "Signé", cls: "badge-success" },
  refuse: { label: "Refusé", cls: "badge-error" },
  expire: { label: "Expiré", cls: "badge-neutral" },
};

function fmtEur(n) {
  return new Intl.NumberFormat("fr-BE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  }).format(Number(n || 0));
}

export default function QuotesListPage() {
  const navigate = useNavigate();
  const { state: authState } = useAuth();
  const isAdmin = hasRole(authState?.user, ["admin"]);
  // Phase B rôle gestionnaire_chantier (2026-09-10) — Un gestionnaire ne peut
  // pas créer de devis initial via la modale "Nouveau devis" (uniquement des
  // avenants, cadré plus tard). On masque le bouton.
  const isGestionnaireChantier = hasRole(authState?.user, ["gestionnaire_chantier"]);
  const currentUserId = authState?.user?.id;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("__all__");
  const [search, setSearch] = useState("");
  // Chantier 3 C11 (2026-09-06) — Filtre "Assigné à" (admin only).
  // Valeurs : "__all__" (défaut admin), "__me__" (mes devis), <user_id> (spécifique).
  const [assignFilter, setAssignFilter] = useState("__all__");
  const [members, setMembers] = useState([]);
  // Chantier 3 items 13-15 (2026-09-06) — Modale "Nouveau devis" avec sélection projet
  const [newQuoteModalOpen, setNewQuoteModalOpen] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    api
      .get("/team/members")
      .then(({ data }) => {
        const arr = Array.isArray(data) ? data : data?.items || [];
        setMembers(arr.filter((m) => hasRole(m, ["admin", "commercial"])));
      })
      .catch(() => setMembers([]));
  }, [isAdmin]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status !== "__all__") params.append("status", status);
      if (search.trim()) params.append("search", search.trim());
      const { data } = await api.get(`/quotes?${params.toString()}`);
      let list = data.items || [];
      // Filtrage client-side pour C11 (admin uniquement — le backend renvoie déjà
      // uniquement les devis assignés au commercial pour ce rôle, cf. A6b).
      if (isAdmin && assignFilter !== "__all__") {
        const target = assignFilter === "__me__" ? currentUserId : assignFilter;
        list = list.filter((q) => (q.assigned_to || null) === target);
      }
      setItems(list);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
     
  }, [status, assignFilter]);

  return (
    <div className="space-y-6" data-testid="quotes-list-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="inline-flex items-center gap-2 text-2xl font-bold">
            <FileText className="h-5 w-5 text-[#C4553B]" /> Devis
          </h1>
          <p className="text-sm text-slate-400">
            Le chantier est créé automatiquement à la signature.
          </p>
        </div>
        <Button
          onClick={() => setNewQuoteModalOpen(true)}
          className="bg-[#C4553B] text-white hover:bg-[#B34A32]"
          data-testid="quotes-new-btn"
          disabled={isGestionnaireChantier}
          style={isGestionnaireChantier ? { display: "none" } : undefined}
        >
          <Plus className="mr-2 h-4 w-4" />
          Nouveau devis
        </Button>
      </div>

      <NewQuoteProjectModal
        open={newQuoteModalOpen}
        onOpenChange={setNewQuoteModalOpen}
      />

      <Card className="border-slate-800 bg-slate-900/40">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-500" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
                placeholder="Rechercher (numéro, client, ville)"
                className="border-slate-700 bg-slate-950/40 pl-9 text-slate-100"
                data-testid="quotes-search-input"
              />
            </div>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-[180px] border-slate-700 bg-slate-950/40" data-testid="quotes-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-slate-700 bg-slate-900">
                <SelectItem value="__all__">Tous les statuts</SelectItem>
                {Object.entries(STATUS_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isAdmin && (
              <Select value={assignFilter} onValueChange={setAssignFilter}>
                <SelectTrigger
                  className="w-[200px] border-slate-700 bg-slate-950/40"
                  data-testid="quotes-assigned-filter"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-slate-700 bg-slate-900">
                  <SelectItem value="__all__" data-testid="quotes-assigned-filter-all">
                    Tous les commerciaux
                  </SelectItem>
                  <SelectItem value="__me__" data-testid="quotes-assigned-filter-me">
                    Mes devis
                  </SelectItem>
                  {members.map((m) => (
                    <SelectItem
                      key={m.id}
                      value={m.id}
                      data-testid={`quotes-assigned-filter-${m.id}`}
                    >
                      {m.first_name} {m.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-800 bg-slate-900/40">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
            </div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center text-sm text-slate-500">
              Aucun devis pour le moment. Ouvrez un chantier pour en créer un.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-800 bg-slate-950/40 text-left text-xs uppercase tracking-wider text-slate-400">
                  <tr>
                    <th className="px-4 py-2.5">Numéro</th>
                    <th className="px-4 py-2.5">Statut</th>
                    <th className="px-4 py-2.5">Client</th>
                    {isAdmin && <th className="px-4 py-2.5">Assigné à</th>}
                    <th className="px-4 py-2.5">Créé le</th>
                    <th className="px-4 py-2.5 text-right">TVAC</th>
                    <th className="px-4 py-2.5 text-right w-12">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((q) => {
                    const meta = STATUS_LABELS[q.status] || STATUS_LABELS.brouillon;
                    return (
                      <tr
                        key={q.id}
                        onClick={() => navigate(`/quotes/${q.id}`)}
                        className="cursor-pointer border-b border-slate-800/60 hover:bg-slate-950/40"
                        data-testid={`quote-row-${q.id}`}
                      >
                        <td className="px-4 py-2.5 font-mono text-slate-200">
                          {q.number ? (
                            q.number
                          ) : (
                            // Phase 2A (2026-09-02) — Brouillon sans numéro → affiche l'intitulé
                            // en italique. Tooltip explicite via `title` HTML natif (mobile-friendly).
                            <span
                              className="italic text-slate-400"
                              title="Numéro attribué à l'envoi"
                              data-testid={`quote-brouillon-title-${q.id}`}
                            >
                              {q.title || "Sans titre"}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2.5">
                          <Badge className={`border ${meta.cls}`}>{meta.label}</Badge>
                        </td>
                        <td className="px-4 py-2.5 text-slate-200">{q.client_display}</td>
                        {isAdmin && (
                          <td className="px-4 py-2.5">
                            <QuoteAssignedBadge userId={q.assigned_to} variant="inline" />
                          </td>
                        )}
                        <td className="px-4 py-2.5 text-slate-400">
                          {new Date(q.created_at).toLocaleDateString("fr-BE")}
                        </td>
                        <td className="tabular px-4 py-2.5 text-right font-semibold text-slate-100">
                          {fmtEur(q.total_tvac)}
                        </td>
                        <td
                          className="px-2 py-2.5 text-right"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <QuoteRowActions quote={q} onChanged={load} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
